import { JsonApiRequestError } from '@farfetched/core';
import { enable } from '@leprecoins/effector';
import { ComboboxItem } from '@mantine/core';
import { combine, createStore, sample, Store } from 'effector';
import { debounce, not, or } from 'patronum';
import {
	createGetCashdeskAllQuery,
	createGetCashiersAllQuery,
	createShiftReportRegistryQuery,
	ShiftReport,
	ShiftReportRegistryFilters,
} from 'shared/api';
import {
	createDateFilterModel,
	createRegistryModel,
	DateFilterModel,
	getToday,
	RegistryModel,
} from 'shared/lib';

export type ShiftReportRegistryTotal = {
	currency: string;
	deposits: number;
	withdrawals: number;
	income: number;
	jackpots: number;
	bonuses: number;
	rtp: number;
	numberOfBets: number;
	incomeAllSources: number;
	rtpAllSources: number;
};

export type CreateShiftReportRegistryModelParams = {
	$currencies: Store<string[]>;
};

export type ShiftReportRegistryModel = {
	registryModel: RegistryModel<ShiftReport, ShiftReportRegistryFilters, JsonApiRequestError>;
	dateFilterModel: DateFilterModel;
	$cashiers: Store<ComboboxItem[]>;
	$cashdesks: Store<ComboboxItem[]>;
	$currencies: Store<string[]>;
	$total: Store<ShiftReportRegistryTotal | null>;
	$isMultiCurrencies: Store<boolean>;
};

export const createShiftReportRegistryModel = ({
	$currencies,
}: CreateShiftReportRegistryModelParams): ShiftReportRegistryModel => {
	const query = createShiftReportRegistryQuery();

	const [todayDate, currentTimeZone] = getToday();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getCashiersAllQuery = createGetCashiersAllQuery();

	const $pending = or(getCashdeskAllQuery.$pending, getCashiersAllQuery.$pending);

	const $firstFetchSended = createStore(false);

	const registryModel = createRegistryModel({
		query,
		disabled: or($pending, not($firstFetchSended)),
		filters: {
			accountIds: [],
			cashdeskIds: [],
			cashierIds: [],
			currencies: [],
			dateFrom: todayDate,
			dateTo: todayDate,
			timeZone: currentTimeZone,
		},
	});

	const dateFilterModel = createDateFilterModel({
		fromChange: registryModel.filters.dateFrom.setValue,
		toChange: registryModel.filters.dateTo.setValue,
	});

	const $cashdesks = getCashdeskAllQuery.$data.map(
		(cashdesks) => cashdesks?.map(({ id, name }) => ({ value: `${id}`, label: `${name}` })) ?? [],
	);

	const $cashiers = getCashiersAllQuery.$data.map(
		(cashiers) => cashiers?.map(({ id, name }) => ({ value: `${id}`, label: `${name}` })) ?? [],
	);

	const $isMultiCurrencies = query.$data.map((data) => {
		const currencies = new Set<string>(data?.rows.map((row) => row.currency) ?? []);

		return currencies.size > 1;
	});

	const $total = combine(
		{
			data: query.$data,
			isMultiCurrencies: $isMultiCurrencies,
		},
		({ data, isMultiCurrencies }) => {
			if (!data) return null;

			const total = data.rows.reduce(
				(acc, row) => ({
					deposits: acc.deposits + row.deposits,
					withdrawals: acc.withdrawals + row.withdrawals,
					jackpots: acc.jackpots + row.jackpots,
					bonuses: acc.bonuses + row.bonuses,
					income: acc.income + row.income,
					rtp: acc.rtp + row.rtp,
					numberOfBets: acc.numberOfBets + row.numberOfBets,
					incomeAllSources: acc.incomeAllSources + row.incomeAllSources,
					rtpAllSources: acc.rtpAllSources + row.rtpAllSources,
				}),
				{
					deposits: 0,
					withdrawals: 0,
					jackpots: 0,
					bonuses: 0,
					income: 0,
					rtp: 0,
					numberOfBets: 0,
					incomeAllSources: 0,
					rtpAllSources: 0,
				},
			);

			return {
				...total,
				income: Number(total.income.toFixed(2)),
				withdrawals: Number(total.withdrawals.toFixed(2)),
				currency: isMultiCurrencies ? '' : (data.rows[0]?.currency ?? ''),
				rtp: total.deposits && Number(((1 - total.income / total.deposits) * 100).toFixed(2)),
				rtpAllSources:
					total.deposits &&
					Number(((1 - total.incomeAllSources / total.deposits) * 100).toFixed(2)),
			};
		},
	);

	sample({
		clock: debounce(registryModel.filters.accountIds.$value, 299),
		filter: registryModel.gate.status,
		target: [getCashdeskAllQuery.start, getCashiersAllQuery.start],
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: registryModel.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: registryModel.filters.cashdeskIds.setValue,
	});

	sample({
		clock: getCashiersAllQuery.finished.success,
		source: { all: $cashiers, current: registryModel.filters.cashierIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashier) => current.includes(Number(cashier.value)))
				.map((cashier) => Number(cashier.value)),
		target: registryModel.filters.cashierIds.setValue,
	});

	sample({
		clock: registryModel.gate.open,
		source: registryModel.filters.accountIds.$value,
		target: [getCashdeskAllQuery.start, getCashiersAllQuery.start],
	});

	sample({
		clock: registryModel.gate.close,
		target: [getCashdeskAllQuery.reset, getCashiersAllQuery.reset],
	});

	enable(getCashdeskAllQuery.start, $firstFetchSended);

	return {
		registryModel,
		dateFilterModel,
		$cashiers,
		$cashdesks,
		$currencies,
		$total,
		$isMultiCurrencies,
	};
};
